/**
 * ContinuityTimelineSummaryPane — Read-only continuity timeline summary
 *
 * Displays replay/continuity progression across recent archived ratchets
 * and Trace Explorer continuity signals.
 *
 * SAFETY: Read-only inspection only. No execution authority, no deployment controls.
 */

import React from 'react';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface ContinuityTimelineSummaryPaneProps {
  model?: {
    // Optional model for future extensibility
  };
}

interface TimelineEntry {
  ratchetId: string;
  ratchetName: string;
  status: 'completed' | 'archived';
  archiveHash: string;
  continuityRole: string;
  immutable: boolean;
  executionAuthority: boolean;
}

const TIMELINE_ENTRIES: TimelineEntry[] = [
  {
    ratchetId: 'governed-improvement-public-verifier-release-archive',
    ratchetName: 'Public Verifier Release',
    status: 'archived',
    archiveHash: '30d7aa5b697aeb053f81a1a156accc02dbb207a002432f1ee4268aaddb7ccf62',
    continuityRole: 'Verifier lineage foundation',
    immutable: true,
    executionAuthority: false,
  },
  {
    ratchetId: 'governed-workspace-improvement-pane-release-archive',
    ratchetName: 'Workspace Improvement Pane',
    status: 'archived',
    archiveHash: 'b4c1fbdef72ac1c42be3eb589c493fa70dfcf51d59160d4d32dd3e8bc08324fe',
    continuityRole: 'Governed UI persistence',
    immutable: true,
    executionAuthority: false,
  },
  {
    ratchetId: 'governed-workspace-trace-explorer-continuity-archive',
    ratchetName: 'Trace Explorer Continuity',
    status: 'archived',
    archiveHash: 'e9de4929153910a819b459a6191cc356ed5e82016461467720eb5bfdbfa996fe',
    continuityRole: 'Replay/continuity inspection',
    immutable: true,
    executionAuthority: false,
  },
  {
    ratchetId: 'governed-workspace-audit-surface-archive',
    ratchetName: 'Audit Summary Surface',
    status: 'archived',
    archiveHash: '88e12aaf4665242895154f5376f17bbc613da823b838fb5592ea3e96bf533951',
    continuityRole: 'Audit surface lineage',
    immutable: true,
    executionAuthority: false,
  },
];

export const ContinuityTimelineSummaryPane: React.FC<ContinuityTimelineSummaryPaneProps> = ({ model }) => {
  const truncateHash = (hash: string) => `${hash.slice(0, 16)}…`;

  const renderTimelineEntry = (entry: TimelineEntry, index: number) => (
    <div key={entry.ratchetId} style={{
      padding: SPACING.sp2,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      borderRadius: 2,
      marginBottom: SPACING.sp2,
      position: 'relative',
    }}>
      {/* Timeline connector line */}
      {index < TIMELINE_ENTRIES.length - 1 && (
        <div style={{
          position: 'absolute',
          left: '24px',
          top: '48px',
          bottom: '-16px',
          width: 2,
          backgroundColor: NEUTRAL.borderSubtle,
          zIndex: 0,
        }} />
      )}

      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: SPACING.sp2,
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Timeline dot */}
        <div style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: SEMANTIC.stabilized.text,
          border: `2px solid ${NEUTRAL.bg200}`,
          flexShrink: 0,
          marginTop: 2,
        }} />

        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: SPACING.sp1,
          }}>
            <span style={{
              fontSize: TYPOGRAPHY.caption.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {entry.ratchetName}
            </span>
            <span style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '1px 4px',
              borderRadius: 2,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}>
              {entry.status}
            </span>
          </div>

          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            fontFamily: TYPOGRAPHY.mono.family,
            marginBottom: SPACING.sp1,
          }}>
            {truncateHash(entry.archiveHash)}
          </div>

          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            {entry.continuityRole}
          </div>

          <div style={{
            display: 'flex',
            gap: SPACING.sp2,
            fontSize: TYPOGRAPHY.micro.size,
          }}>
            <span style={{ color: entry.immutable ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
              immutable: {String(entry.immutable)}
            </span>
            <span style={{ color: !entry.executionAuthority ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
              executionAuthority: {String(entry.executionAuthority)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      padding: SPACING.sp3,
      height: '100%',
      overflowY: 'auto',
      backgroundColor: NEUTRAL.bg200,
    }}>
      {/* Header */}
      <div style={{
        marginBottom: SPACING.sp3,
        paddingBottom: SPACING.sp2,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp1,
        }}>
          CONTINUITY TIMELINE
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.caption.size,
          color: NEUTRAL.text400,
        }}>
          Read-only timeline of archived ratchets and continuity progression
        </div>
      </div>

      {/* Summary counters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: SPACING.sp2,
        marginBottom: SPACING.sp3,
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
      }}>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Archived Ratchets
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
          }}>
            4
          </div>
        </div>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Active Ratchets
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
          }}>
            0
          </div>
        </div>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Replayable Lineage
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
          }}>
            true
          </div>
        </div>
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Execution Authority
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
          }}>
            false
          </div>
        </div>
      </div>

      {/* Timeline entries */}
      <div style={{ marginBottom: SPACING.sp3 }}>
        {TIMELINE_ENTRIES.map((entry, index) => renderTimelineEntry(entry, index))}
      </div>

      {/* Safety labels */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          marginBottom: SPACING.sp1,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          Safety Labels
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <span>• Read-only timeline</span>
          <span>• Immutable archived lineage</span>
          <span>• No execution authority</span>
          <span>• No deploy or rollback execution</span>
        </div>
      </div>

      {/* Read-only notice */}
      <div style={{
        marginTop: SPACING.sp3,
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
      }}>
        <strong>Read-only inspection only.</strong> This timeline displays archived ratchet progression only. It does not deploy, rollback, mutate state, or grant execution authority.
      </div>
    </div>
  );
};
